// src/pages/DictionaryEdit.jsx
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";
import GitHubIcon from "@mui/icons-material/GitHub";
import DownloadIcon from "@mui/icons-material/Download";
import useUndo from "use-undo";
import {
  getDictionary,
  getDictionaryContent,
  updateDictionary,
  saveDictionaryContent,
  createDictionaryFromText,
  authHeaders,
  exportToGitHub,
  getGitHubStatus,
  getGitHubRepositories,
  getGitHubBranches,
} from "../utils/api";
import { API_BASE } from "../utils/env";
import DictionaryBuilder from "../components/DictionaryBuilder";
import { yamlToTables, tablesToYaml } from "../utils/schemaFormatters";

export default function DictionaryEdit({ mode = "live" }) {
  const nav = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === "new";
  const isSnapshot = mode === "snapshot";

  const [name, setName] = useState("");
  const [viewMode, setViewMode] = useState("rendered"); // rendered | raw (YAML)
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  const [toast, setToast] = useState({ open: false, msg: "", sev: "info" });
  const openToast = (sev, msg) => setToast({ open: true, sev, msg });

  const [contentState, { set: setContent }] = useUndo(""); // YAML string
  const yamlContent = contentState.present;
  const [tables, setTables] = useState([]);

  // GitHub export state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportRepoOwner, setExportRepoOwner] = useState("");
  const [exportRepoName, setExportRepoName] = useState("");
  const [exportRepoPath, setExportRepoPath] = useState("");
  const [exportBranch, setExportBranch] = useState("main");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [githubConnected, setGithubConnected] = useState(false);
  const [githubLogin, setGithubLogin] = useState("");
  const [repositories, setRepositories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // --- Validation ---
    const validateForeignKeys = useCallback((tables) => {
      const errors = [];
      const tableMap = new Map();
    
      // Build normalized lowercase table/column map
      tables.forEach((t) => {
        tableMap.set(
          t.name.toLowerCase(),
          t.columns.map((c) => c.name.toLowerCase())
        );
      });
    
      tables.forEach((table, tIdx) => {
        table.columns.forEach((col, cIdx) => {
          if (col.key === "FK") {
            const refTable = col.refTable?.trim().toLowerCase();
            const refColumn = col.refColumn?.trim().toLowerCase();
    
            if (!refTable || !refColumn) {
              errors.push(`Row ${tIdx + 1}.${cIdx + 1}: FK reference is incomplete.`);
              return;
            }
    
            const tableExists = tableMap.has(refTable);
            const columnExists = tableMap.get(refTable)?.includes(refColumn);
    
            if (!tableExists) {
              errors.push(
                `Row ${tIdx + 1}.${cIdx + 1}: FK refers to non-existent table "${col.refTable}".`
              );
            } else if (!columnExists) {
              errors.push(
                `Row ${tIdx + 1}.${cIdx + 1}: FK refers to missing column "${col.refColumn}" in table "${col.refTable}".`
              );
            }
          }
        });
      });
    
      return errors;
    }, []);

  useEffect(() => {
    setValidationErrors(validateForeignKeys(tables));
  }, [tables, validateForeignKeys]);

  // --- Load ---
  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      if (isSnapshot) {
        const headers = await authHeaders();
        const res = await fetch(`${process.env.REACT_APP_API_BASE}/runs/${id}/dictionary`, { headers });
        if (!res.ok) throw new Error(`Snapshot fetch failed: ${res.status}`);
        const yaml = await res.text();
        const parsed = yamlToTables(yaml);
        setContent(yaml || "");
        setTables(parsed);
        setName(`Snapshot for run ${id}`);
      } else {
        const meta = await getDictionary(id);
        setName(meta.name || meta.filename || "");
        const yaml = await getDictionaryContent(id);
        const parsed = yamlToTables(yaml);
        setContent(yaml || "");
        setTables(parsed);
      }
    } catch (e) {
      console.error("[DictionaryEdit] load error:", e);
      openToast("error", "Failed to load dictionary.");
      setTimeout(() => nav(isSnapshot ? "/runs" : "/dictionaries"), 300);
    } finally {
      setLoading(false);
    }
  }, [id, isNew, isSnapshot, nav, setContent]);

  useEffect(() => {
    if (!isNew) load();
  }, [isNew, load]);

  // Check GitHub connection status
  useEffect(() => {
    const checkGitHub = async () => {
      try {
        const status = await getGitHubStatus();
        setGithubConnected(status?.connected || false);
        setGithubLogin(status?.github_login || "");
        if (status?.connected && status?.github_login) {
          setExportRepoOwner(status.github_login);
        }
      } catch (e) {
        console.error("Failed to check GitHub status:", e);
        setGithubConnected(false);
      }
    };
    checkGitHub();
  }, []);

  // Load repositories when dialog opens
  useEffect(() => {
    if (exportDialogOpen && githubConnected) {
      const loadRepos = async () => {
        setLoadingRepos(true);
        try {
          const data = await getGitHubRepositories();
          setRepositories(data?.repositories || []);
        } catch (e) {
          console.error("Failed to load repositories:", e);
          setRepositories([]);
        } finally {
          setLoadingRepos(false);
        }
      };
      loadRepos();
    }
  }, [exportDialogOpen, githubConnected]);

  // Load branches when repository is selected
  useEffect(() => {
    if (exportRepoOwner && exportRepoName && githubConnected) {
      const loadBranches = async () => {
        setLoadingBranches(true);
        try {
          const data = await getGitHubBranches(exportRepoOwner, exportRepoName);
          const branchList = data?.branches || [];
          setBranches(branchList);
          if (branchList.length > 0) {
            const mainBranch = branchList.find(b => b === "main") || branchList.find(b => b === "master") || branchList[0];
            setExportBranch(mainBranch);
          } else {
            setExportBranch("");
          }
        } catch (e) {
          console.error("Failed to load branches:", e);
          setBranches([]);
          setExportBranch("");
          setExportError(`Failed to load branches: ${e?.response?.data?.error || e?.message || "Unknown error"}`);
        } finally {
          setLoadingBranches(false);
        }
      };
      loadBranches();
    } else {
      setBranches([]);
      setExportBranch("");
    }
  }, [exportRepoOwner, exportRepoName, githubConnected]);

  // Auto-suggest file path when dictionary id or repo changes
  useEffect(() => {
    if (id && !isNew && exportRepoName && !exportRepoPath) {
      const dictName = (name || "dictionary").replace(/\s+/g, "-").toLowerCase();
      setExportRepoPath(`dictionaries/${dictName}.yaml`);
    }
  }, [id, isNew, exportRepoName, exportRepoPath, name]);

  // --- Switch between rendered and YAML raw view ---
  const handleSwitchMode = (nextMode) => {
    if (!nextMode) return;
    if (nextMode === "raw") {
      // Convert tables → YAML string
      const yamlStr = tablesToYaml(tables);
      setContent(yamlStr);
      setViewMode("raw");
    } else if (nextMode === "rendered") {
      setParsing(true);
      setViewMode("rendered");
    }
  };

  useEffect(() => {
    if (viewMode === "rendered" && parsing) {
      setTimeout(() => {
        const parsed = yamlToTables(yamlContent);
        setTables(parsed);
        setParsing(false);
      }, 100);
    }
  }, [parsing, viewMode, yamlContent]);

  // --- Save / Create ---
  const saveAll = async () => {
    if (isNew) return;
    const trimmed = (name || "").trim();
    if (!trimmed) return openToast("warning", "Name is required.");
    setSaving(true);
    try {
      const yamlToSave = viewMode === "rendered" ? tablesToYaml(tables) : yamlContent;
      await updateDictionary(id, { name: trimmed });
      await saveDictionaryContent(id, yamlToSave);
      openToast("success", "Saved.");
    } catch (e) {
      console.error("[DictionaryEdit] save error:", e);
      openToast("error", "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  const createNow = async () => {
    const trimmed = name.trim();
    if (!trimmed) return openToast("warning", "Please enter a name.");
    setCreating(true);
    try {
      const yamlToSave = viewMode === "rendered" ? tablesToYaml(tables) : yamlContent;
      const created = await createDictionaryFromText(trimmed, yamlToSave);
      const newId = created?.id || created?.dict_id;
      openToast("success", "Created.");
      setTimeout(() => nav(`/dictionaries/${newId}`), 200);
    } catch (e) {
      console.error("[DictionaryEdit] create error:", e);
      openToast("error", "Failed to create dictionary.");
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async () => {
    if (!id || isNew) return;
    try {
      const headers = await authHeaders();
      const url = isSnapshot 
        ? `${API_BASE}/runs/${id}/dictionary`
        : `${API_BASE}/dictionaries/${encodeURIComponent(id)}/content`;
      
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Try to infer filename from header; fall back to sensible default
      const cd = res.headers.get("content-disposition") || "";
      let filename = (cd.match(/filename\*=UTF-8''([^;]+)$/i)?.[1]
        || cd.match(/filename="?([^";]+)"?/i)?.[1]
        || (name || `dictionary-${id}`).replace(/\s+/g, "-").toLowerCase() + ".yaml");

      // Decode RFC5987 if present
      try { filename = decodeURIComponent(filename); } catch {}

      const blob = await res.blob();
      const urlObj = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = urlObj;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(urlObj);
    } catch (e) {
      console.error("Failed to download dictionary", e);
      openToast("error", e.message || "Failed to download dictionary");
    }
  };

  const handleExportToGitHub = async () => {
    if (!id || isNew) return;
    try {
      setExporting(true);
      setExportError("");
      const payload = {
        dictId: id,
        repoOwner: exportRepoOwner,
        repoName: exportRepoName,
        repoPath: exportRepoPath || `dictionaries/${(name || "dictionary").replace(/\s+/g, "-").toLowerCase()}.yaml`,
        branch: exportBranch,
        type: "dictionary",
      };
      console.log("[Dictionary Export] Sending request:", payload);
      await exportToGitHub(payload);
      openToast("success", "Dictionary exported to GitHub successfully!");
      setExportDialogOpen(false);
      // Reset form (but keep owner as GitHub username)
      setExportRepoName("");
      setExportRepoPath("");
      setExportBranch("main");
      setBranches([]);
    } catch (e) {
      console.error("[Dictionary Export] Error:", e);
      console.error("[Dictionary Export] Error response:", e?.response?.data);
      console.error("[Dictionary Export] Error status:", e?.response?.status);
      setExportError(e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  // --- UI ---
  return (
    <Box sx={{ mx: "auto", px: 2 }}>
      <Typography variant="h4" fontWeight={600} mb={2}>
        {isNew ? "New Dictionary" : isSnapshot ? "Snapshot Dictionary" : "Edit Dictionary"}
      </Typography>

      <Stack spacing={2} mb={2}>
        <TextField
          label="Name"
          fullWidth
          required
          value={name}
          onChange={(e) => !isSnapshot && setName(e.target.value)}
          InputProps={{ readOnly: isSnapshot }}
        />
      </Stack>

      {/* Toggle between YAML and Table View */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, val) => val && handleSwitchMode(val)}
          size="small"
          color="primary"
        >
          <ToggleButton value="rendered">Table View</ToggleButton>
          <ToggleButton value="raw">YAML View</ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" spacing={2}>
          {!isNew && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={loading}
            >
              Download .yaml
            </Button>
          )}
          {!isSnapshot && (
            <>
              {githubConnected && !isNew && (
                <Button
                  variant="outlined"
                  startIcon={<GitHubIcon />}
                  onClick={() => setExportDialogOpen(true)}
                  disabled={saving || creating || loading}
                >
                  Export to GitHub
                </Button>
              )}
              <Button variant="outlined" onClick={() => nav("/dictionaries")} disabled={saving || creating}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={isNew ? createNow : saveAll}
                disabled={saving || creating || loading || validationErrors.length > 0}
              >
                {isNew ? (creating ? "Creating…" : "Save & Continue") : saving ? "Saving…" : "Save"}
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {/* Validation Errors */}
      {!isSnapshot && validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={500}>
            Fix the following foreign key issues before saving:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationErrors.map((err, idx) => (
              <li key={idx}>
                <Typography variant="body2">{err}</Typography>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/* View Modes */}
      {viewMode === "raw" ? (
        <TextField
          multiline
          minRows={20}
          fullWidth
          value={yamlContent}
          onChange={(e) => !isSnapshot && setContent(e.target.value)}
          InputProps={{
            style: {
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, monospace",
            },
            readOnly: isSnapshot,
          }}
        />
      ) : parsing ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress />
          <Typography mt={2}>Parsing YAML…</Typography>
        </Box>
      ) : (
        <DictionaryBuilder
          tables={tables}
          setTables={isSnapshot ? () => {} : setTables}
          readOnly={isSnapshot}
        />
      )}

      {/* Export to GitHub Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Dictionary to GitHub</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {exportError && (
              <Alert severity="error" onClose={() => setExportError("")}>
                {exportError}
              </Alert>
            )}
            <TextField
              label="Repository Owner"
              value={exportRepoOwner}
              onChange={(e) => {
                setExportRepoOwner(e.target.value);
                setExportRepoName("");
                setBranches([]);
              }}
              placeholder="username or org"
              fullWidth
              required
              helperText={githubLogin ? `Connected as ${githubLogin}` : ""}
            />
            <TextField
              label="Repository"
              value={
                (() => {
                  const matchedRepo = repositories.find(r => r.name === exportRepoName && r.owner === exportRepoOwner);
                  return matchedRepo ? matchedRepo.full_name : exportRepoName;
                })()
              }
              onChange={(e) => {
                const value = e.target.value;
                const selectedByFullName = repositories.find(r => r.full_name === value);
                if (selectedByFullName) {
                  setExportRepoOwner(selectedByFullName.owner);
                  setExportRepoName(selectedByFullName.name);
                  return;
                }
                const matchByOwner = repositories.find(r => r.name === value && r.owner === exportRepoOwner);
                if (matchByOwner) {
                  setExportRepoName(matchByOwner.name);
                  return;
                }
                const matchByName = repositories.find(r => r.name === value);
                if (matchByName) {
                  setExportRepoOwner(matchByName.owner);
                  setExportRepoName(matchByName.name);
                  return;
                }
                setExportRepoName(value);
              }}
              select={repositories.length > 0}
              fullWidth
              required
              disabled={loadingRepos}
              helperText={
                loadingRepos
                  ? "Loading repositories..."
                  : repositories.length > 0
                    ? `Select from ${repositories.length} repositories or type repository name`
                    : "No repositories found"
              }
            >
              {repositories.map((repo) => (
                <MenuItem key={repo.full_name} value={repo.full_name}>
                  {repo.full_name} {repo.private ? "🔒" : ""}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="File Path"
              value={exportRepoPath}
              onChange={(e) => setExportRepoPath(e.target.value)}
              placeholder={`dictionaries/${(name || "dictionary").replace(/\s+/g, "-").toLowerCase()}.yaml`}
              fullWidth
              helperText="Path where the file will be created/updated"
            />
            <TextField
              label="Branch"
              value={exportBranch}
              onChange={(e) => setExportBranch(e.target.value)}
              select
              fullWidth
              disabled={loadingBranches || !exportRepoOwner || !exportRepoName}
              helperText={
                loadingBranches
                  ? "Loading branches..."
                  : !exportRepoOwner || !exportRepoName
                    ? "Select repository first"
                    : branches.length === 0
                      ? "No branches found or access denied"
                      : `${branches.length} branch(es) available`
              }
            >
              {branches.map((branch) => (
                <MenuItem key={branch} value={branch}>
                  {branch}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} disabled={exporting}>
            Cancel
          </Button>
          <Button
            onClick={handleExportToGitHub}
            variant="contained"
            disabled={exporting || !exportRepoOwner || !exportRepoName}
          >
            {exporting ? "Exporting..." : "Export"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}